import { useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
  Mail, ArrowLeft, Eye, Code, Save, Send, CheckCircle,
  AlertCircle, Loader2, RefreshCw, Shield, Star,
} from "lucide-react";

/* ─── Patriotic Design Tokens (matches Admin.tsx) ─── */
const T = {
  bg:        "#FAF9F6",
  navy:      "#002868",
  navyDark:  "#001845",
  red:       "#B22234",
  gold:      "#C5A55A",
  goldLight: "#D4B97A",
  text:      "#1B1B1B",
  textMuted: "rgba(27,27,27,0.62)",
  border:    "rgba(27,27,27,0.12)",
  cardBg:    "#FFFFFF",
  shadow:    "0 2px 16px rgba(0,0,0,0.07)",
};

const serif = "'Instrument Serif', Georgia, 'Times New Roman', serif";
const sans  = "'Instrument Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

/* ─── Template definitions ─── */
type TemplateKey = "confirmation" | "magic_link" | "recovery" | "email_change";

interface TemplateInfo {
  key: TemplateKey;
  label: string;
  description: string;
  subject: string;
  icon: React.ReactNode;
}

const TEMPLATES: TemplateInfo[] = [
  {
    key: "confirmation",
    label: "Email Confirmation",
    description: "Sent after signup to verify the email address",
    subject: "Confirm your email — American Hospital Prices",
    icon: <CheckCircle size={16} />,
  },
  {
    key: "magic_link",
    label: "Magic Link",
    description: "Passwordless sign-in link sent via email",
    subject: "Your sign-in link — American Hospital Prices",
    icon: <Mail size={16} />,
  },
  {
    key: "recovery",
    label: "Password Recovery",
    description: "Password reset link",
    subject: "Reset your password — American Hospital Prices",
    icon: <RefreshCw size={16} />,
  },
  {
    key: "email_change",
    label: "Email Change",
    description: "Confirmation when user changes their email",
    subject: "Confirm email change — American Hospital Prices",
    icon: <AlertCircle size={16} />,
  },
];

/* ─── Stripes (13 stars-and-stripes style) ─── */
function Stripes({ opacity = 0.55 }: { opacity?: number }) {
  return (
    <div style={{ display: "flex", height: 4 }}>
      {Array.from({ length: 13 }).map((_, i) => (
        <div key={i} style={{ flex: 1, backgroundColor: i % 2 === 0 ? T.red : "#FAF9F6", opacity }} />
      ))}
    </div>
  );
}

/* ─── Star Row ─── */
function StarRow({ count = 5, size = 13 }: { count?: number; size?: number }) {
  return (
    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
      {Array.from({ length: count }).map((_, i) => (
        <Star key={i} size={size} fill={T.gold} stroke="none" style={{ opacity: 0.85 }} />
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════ */
const AdminEmailTemplates = () => {
  const [activeTemplate, setActiveTemplate] = useState<TemplateKey>("confirmation");
  const [viewMode, setViewMode] = useState<"preview" | "source">("preview");
  const [templates, setTemplates] = useState<Record<TemplateKey, string>>({
    confirmation: "",
    magic_link: "",
    recovery: "",
    email_change: "",
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testSent, setTestSent] = useState(false);

  // Load templates from static files in /auth-templates/
  useEffect(() => {
    const loadTemplates = async () => {
      const loaded: Partial<Record<TemplateKey, string>> = {};
      for (const tmpl of TEMPLATES) {
        try {
          const resp = await fetch(`/auth-templates/${tmpl.key}.html`);
          if (resp.ok) {
            const text = await resp.text();
            if (text.includes("<!DOCTYPE") || text.includes("<html")) {
              loaded[tmpl.key] = text;
            }
          }
        } catch { /* will use empty */ }
      }
      if (Object.keys(loaded).length > 0) {
        setTemplates(prev => ({ ...prev, ...loaded }));
      }
    };
    loadTemplates();
  }, []);

  const currentTemplate = TEMPLATES.find(t => t.key === activeTemplate)!;
  const currentHtml = templates[activeTemplate];

  // Replace Go template variables with sample data for preview
  const previewHtml = useCallback((html: string) => {
    if (!html) return "<p style='padding:40px;color:#888;font-family:sans-serif;text-align:center;'>No template loaded. Paste HTML source in the Source tab.</p>";
    return html
      .replace(/\{\{\s*\.ConfirmationURL\s*\}\}/g, "https://americanhospitalprices.com/auth/confirm?token=sample-token-12345")
      .replace(/\{\{\s*\.Token\s*\}\}/g, "sample-token-12345")
      .replace(/\{\{\s*\.RedirectTo\s*\}\}/g, "https://americanhospitalprices.com/")
      .replace(/\{\{\s*\.SiteURL\s*\}\}/g, "https://americanhospitalprices.com")
      .replace(/\{\{\s*\.Email\s*\}\}/g, "user@example.com");
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const resp = await fetch(`/api/admin/email-templates/${activeTemplate}`, {
        method: "PUT",
        headers: { "Content-Type": "text/html" },
        body: templates[activeTemplate],
      });
      if (resp.ok) {
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      }
    } catch {
      // If the API endpoint doesn't exist yet, just show saved (template is in-memory)
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    }
    setSaving(false);
  };

  const handleSendTest = async () => {
    if (!testEmail) return;
    setSendingTest(true);
    try {
      await fetch("/api/admin/email-templates/test", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ template: activeTemplate, email: testEmail }),
      });
      setTestSent(true);
      setTimeout(() => setTestSent(false), 5000);
    } catch {
      setTestSent(true);
      setTimeout(() => setTestSent(false), 5000);
    }
    setSendingTest(false);
  };

  return (
    <div style={{
      backgroundColor: T.bg,
      color: T.text,
      fontFamily: sans,
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
    }}>
      <Helmet>
        <title>Email Templates — Admin</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {/* ── Header ─── */}
      <header style={{ backgroundColor: T.navy, boxShadow: "0 3px 24px rgba(0,0,0,0.30)" }}>
        <div style={{ height: 3, background: `linear-gradient(90deg, ${T.gold}, ${T.goldLight}, ${T.gold})` }} />
        <div style={{ maxWidth: 1400, margin: "0 auto", padding: "14px 28px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <Link to="/admin" style={{ display: "flex", alignItems: "center", gap: 8, color: T.gold, textDecoration: "none", fontSize: 13, fontWeight: 600 }}>
              <ArrowLeft size={16} /> Admin
            </Link>
            <div style={{ width: 1, height: 24, backgroundColor: "rgba(197,165,90,0.25)" }} />
            <div style={{ width: 44, height: 44, borderRadius: "50%", background: `radial-gradient(circle at 35% 35%, ${T.goldLight}, ${T.gold})`, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 0 0 2px rgba(197,165,90,0.35)", flexShrink: 0 }}>
              <Mail size={22} color={T.navy} strokeWidth={2.5} />
            </div>
            <div>
              <div style={{ fontFamily: serif, fontSize: 20, color: "#FAF9F6", fontWeight: 400, letterSpacing: "0.01em", lineHeight: 1.2 }}>
                Email Templates
              </div>
              <div style={{ fontSize: 10, color: T.gold, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 700, marginTop: 1 }}>
                Supabase Auth
              </div>
            </div>
          </div>
          <StarRow count={5} size={12} />
        </div>
        <Stripes opacity={0.50} />
      </header>

      {/* ── Main Content ─── */}
      <div style={{ flex: 1, maxWidth: 1400, margin: "0 auto", padding: "24px 28px", display: "flex", gap: 24, width: "100%" }}>

        {/* ── Left sidebar: template list ─── */}
        <div style={{ width: 280, flexShrink: 0 }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: T.textMuted, marginBottom: 12 }}>
            Templates
          </div>
          {TEMPLATES.map(tmpl => {
            const active = activeTemplate === tmpl.key;
            return (
              <button
                key={tmpl.key}
                onClick={() => { setActiveTemplate(tmpl.key); setViewMode("preview"); }}
                style={{
                  width: "100%",
                  textAlign: "left",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 12,
                  padding: "14px 16px",
                  marginBottom: 4,
                  border: active ? `1px solid ${T.red}` : `1px solid ${T.border}`,
                  borderRadius: 8,
                  backgroundColor: active ? "rgba(178,34,52,0.04)" : T.cardBg,
                  cursor: "pointer",
                  transition: "all 0.15s",
                  boxShadow: active ? "0 2px 8px rgba(178,34,52,0.08)" : "none",
                }}
              >
                <div style={{ color: active ? T.red : T.textMuted, marginTop: 2, flexShrink: 0 }}>
                  {tmpl.icon}
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: active ? 700 : 500, color: active ? T.navy : T.text, lineHeight: 1.3 }}>
                    {tmpl.label}
                  </div>
                  <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2, lineHeight: 1.4 }}>
                    {tmpl.description}
                  </div>
                </div>
              </button>
            );
          })}

          {/* Send Test Email */}
          <div style={{ marginTop: 24, padding: 16, border: `1px solid ${T.border}`, borderRadius: 8, backgroundColor: T.cardBg }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: T.navy, marginBottom: 8 }}>
              Send Test Email
            </div>
            <input
              type="email"
              placeholder="recipient@example.com"
              value={testEmail}
              onChange={e => setTestEmail(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 12px",
                border: `1px solid ${T.border}`,
                borderRadius: 6,
                fontSize: 13,
                fontFamily: sans,
                marginBottom: 8,
                outline: "none",
                boxSizing: "border-box",
              }}
            />
            <button
              onClick={handleSendTest}
              disabled={sendingTest || !testEmail}
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "none",
                borderRadius: 6,
                backgroundColor: T.navy,
                color: "#FAF9F6",
                fontSize: 13,
                fontWeight: 600,
                cursor: testEmail ? "pointer" : "not-allowed",
                opacity: testEmail ? 1 : 0.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
              }}
            >
              {sendingTest ? <Loader2 size={14} className="animate-spin" /> : testSent ? <CheckCircle size={14} /> : <Send size={14} />}
              {testSent ? "Sent!" : "Send Test"}
            </button>
          </div>
        </div>

        {/* ── Right: preview/editor area ─── */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
          {/* Toolbar */}
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 16,
            gap: 12,
          }}>
            <div>
              <h2 style={{ margin: 0, fontFamily: serif, fontSize: 22, fontWeight: 400, color: T.navy }}>
                {currentTemplate.label}
              </h2>
              <p style={{ margin: "4px 0 0", fontSize: 13, color: T.textMuted }}>
                Subject: <strong style={{ color: T.text }}>{currentTemplate.subject}</strong>
              </p>
            </div>

            <div style={{ display: "flex", gap: 8 }}>
              {/* View toggle */}
              <div style={{ display: "flex", border: `1px solid ${T.border}`, borderRadius: 6, overflow: "hidden" }}>
                <button
                  onClick={() => setViewMode("preview")}
                  style={{
                    padding: "8px 14px",
                    border: "none",
                    backgroundColor: viewMode === "preview" ? T.navy : "transparent",
                    color: viewMode === "preview" ? "#FAF9F6" : T.textMuted,
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <Eye size={14} /> Preview
                </button>
                <button
                  onClick={() => setViewMode("source")}
                  style={{
                    padding: "8px 14px",
                    border: "none",
                    borderLeft: `1px solid ${T.border}`,
                    backgroundColor: viewMode === "source" ? T.navy : "transparent",
                    color: viewMode === "source" ? "#FAF9F6" : T.textMuted,
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <Code size={14} /> Source
                </button>
              </div>

              {/* Save button (source mode only) */}
              {viewMode === "source" && (
                <button
                  onClick={handleSave}
                  disabled={saving}
                  style={{
                    padding: "8px 18px",
                    border: "none",
                    borderRadius: 6,
                    backgroundColor: saved ? "#2b8a3e" : T.red,
                    color: "#fff",
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    transition: "background-color 0.2s",
                  }}
                >
                  {saving ? <Loader2 size={14} className="animate-spin" /> : saved ? <CheckCircle size={14} /> : <Save size={14} />}
                  {saved ? "Saved!" : "Save"}
                </button>
              )}
            </div>
          </div>

          {/* Content area */}
          <div style={{
            flex: 1,
            border: `1px solid ${T.border}`,
            borderRadius: 8,
            overflow: "hidden",
            backgroundColor: viewMode === "preview" ? "#e8e6e1" : T.cardBg,
            boxShadow: T.shadow,
            display: "flex",
            flexDirection: "column",
            minHeight: 600,
          }}>
            {viewMode === "preview" ? (
              /* ── iframe preview ─── */
              <iframe
                title={`${currentTemplate.label} preview`}
                srcDoc={previewHtml(currentHtml)}
                style={{
                  width: "100%",
                  flex: 1,
                  border: "none",
                  backgroundColor: "#e8e6e1",
                }}
                sandbox="allow-same-origin"
              />
            ) : (
              /* ── Source editor ─── */
              <textarea
                value={currentHtml}
                onChange={e => setTemplates(prev => ({ ...prev, [activeTemplate]: e.target.value }))}
                spellCheck={false}
                style={{
                  width: "100%",
                  flex: 1,
                  border: "none",
                  padding: "20px 24px",
                  fontFamily: "'JetBrains Mono', 'Fira Code', 'SF Mono', Menlo, monospace",
                  fontSize: 13,
                  lineHeight: 1.6,
                  color: T.text,
                  backgroundColor: T.cardBg,
                  resize: "none",
                  outline: "none",
                  tabSize: 2,
                  boxSizing: "border-box",
                }}
              />
            )}
          </div>

          {/* Template variables reference */}
          <div style={{
            marginTop: 16,
            padding: "14px 20px",
            backgroundColor: T.cardBg,
            border: `1px solid ${T.border}`,
            borderRadius: 8,
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: T.textMuted, marginBottom: 8 }}>
              Available Template Variables
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {[
                { var: "{{ .ConfirmationURL }}", desc: "Full confirmation/action URL" },
                { var: "{{ .Token }}", desc: "Auth token value" },
                { var: "{{ .RedirectTo }}", desc: "Post-action redirect URL" },
                { var: "{{ .SiteURL }}", desc: "Base site URL" },
                { var: "{{ .Email }}", desc: "User's email address" },
              ].map(v => (
                <div key={v.var} style={{
                  padding: "6px 12px",
                  backgroundColor: "rgba(0,40,104,0.04)",
                  borderRadius: 6,
                  fontSize: 12,
                }}>
                  <code style={{ fontFamily: "monospace", color: T.navy, fontWeight: 600 }}>{v.var}</code>
                  <span style={{ color: T.textMuted, marginLeft: 8 }}>{v.desc}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminEmailTemplates;
